import findClosestScrollContainer from "../../utilities/findClosestScrollContainer.js";
import debounce from "../../utilities/debounce.js";

export default (dotnetRef, element, rootId, topGroup) => {
  topGroup = topGroup || 'default';

  if (!element) return {};

  const root = window.document.getElementById(rootId) || findClosestScrollContainer(element) || null;
  console.log('[IntersectionObserver] Root: ', root);

  let scrollDirection = '';
  let lastScrollTime = 0; // timestamp of the most recent user scroll

  const getCurrentScrollPosition = () => {
    const value = root ? root.scrollY || root.scrollTop : window.scrollY || document.documentElement.scrollTop;
    return Math.trunc(value);
  };

  const defaultScrollContainerRect = {top: 0};
  const isElementTopAboveViewport = (element) => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const scrollContainerRect = root ? root.getBoundingClientRect() : defaultScrollContainerRect;
    return rect.top < scrollContainerRect.top;
  };

  const getTopGroupElement = (node) => {
    while (node && node !== document) {
      if (node.dataset && node.dataset.group === topGroup) {
        return node; // Parent with matching 'data-group' found
      }
      node = node.parentNode; // Traverse to the parent
    }
    return null; // No parent with matching 'data-group' found
  };

  const show = async (node) => {
    if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE || node.classList.contains('ra-intersection-in')) return;

    await dotnetRef.invokeMethodAsync('IntersectionChanged', {
      isIntersecting: true, targetId: node.id, top: node.offsetTop, rootScrollPosition: getCurrentScrollPosition(),
    });

    node.classList.add('ra-intersection-in');
    node.style.minHeight = 'auto';
    node.dataset.height = node.offsetHeight;
    node.dataset.lastShown = '' + Date.now();

    // Only compensate layout shifts when the user is actively scrolling up
    const recentlyScrolled = Date.now() - lastScrollTime < 200;
    if (scrollDirection !== 'up' || !recentlyScrolled || !isElementTopAboveViewport(node)) return;

    const topElement = getTopGroupElement(node);
    if (topElement === null) return;

    const prevHeight = Number.parseFloat(topElement.dataset.height);
    if (!Number.isFinite(prevHeight)) {
      // Initialize stored height on first encounter to avoid accidental jumps
      topElement.dataset.height = topElement.offsetHeight;
      return;
    }

    const delta = topElement.offsetHeight - prevHeight;
    if (Math.abs(Math.round(delta)) === 0) return;

    console.log('[IntersectionObserver] Adjusting scroll position by ' + delta + 'px');
    topElement.dataset.height = topElement.offsetHeight;
    if (root) {
      root.scrollTop += delta;
    } else {
      // window.scrollBy({top: delta, behavior: 'instant'});
    }
  };

  const hide = async (node) => {
    if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE) return;

    // Skip if recently shown (avoids frequent toggles)
    if (node.dataset.lastShown && Date.now() - node.dataset.lastShown <= 1000) return;

    // Store height of the node for a consistent layout
    node.dataset.height = node.offsetHeight;
    node.style.minHeight = `${node.offsetHeight || node.dataset.height}px`;

    // Notify .NET backend
    await dotnetRef.invokeMethodAsync('IntersectionChanged', {
      isIntersecting: false, targetId: node.id, top: node.offsetTop, rootScrollPosition: getCurrentScrollPosition(),
    });

    node.classList.remove('ra-intersection-in');
  };

  const intersectionCallback = async (entries) => {
    for (let i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        await show(entries[i].target);
      } else {
        await hide(entries[i].target);
      }
    }
  };

  const intersectionObserver = new IntersectionObserver(intersectionCallback, {
      root: root, rootMargin: '2000px 5000px 2000px 5000px', threshold: 0.000001,
  });

  const observe = async (node) => {
    if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    intersectionObserver.observe(node);
    node.style.minHeight = node.dataset.height + 'px';
  };

  const unobserve = (node) => {
    if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    intersectionObserver.unobserve(node);
  };

  let lastScrollTop = getCurrentScrollPosition();
  const updateScrollToBlazor = debounce(async () => {
    await dotnetRef.invokeMethodAsync('IntersectionChanged', {
      isIntersecting: false, targetId: null, top: element.offsetTop, rootScrollPosition: getCurrentScrollPosition(),
    });
  }, 100);
  const scrollHandler = () => {
    const currentScrollTop = getCurrentScrollPosition();

    if (currentScrollTop > lastScrollTop) {
      scrollDirection = 'down';
    } else if (currentScrollTop < lastScrollTop) {
      scrollDirection = 'up';
    }

    lastScrollTop = currentScrollTop;
    lastScrollTime = Date.now();

    updateScrollToBlazor();
  };
  (root || window).addEventListener('scroll', scrollHandler);

  const dispose = () => {
    intersectionObserver.disconnect();
    (root || window).removeEventListener('scroll', scrollHandler);
  };

  return {
    observe, unobserve, dispose,
  };
};
